import { useCallback, useEffect, useRef, useState } from "react";
import { RefreshCw } from "lucide-react";
import type { SetupSnapshot } from "../shared/contracts";
import { request } from "./api";

export function AdminSetup({ viewerId }: { viewerId: string }) {
  const [setup, setSetup] = useState<SetupSnapshot | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const mounted = useRef(false);
  const requestId = useRef(0);
  const load = useCallback(async () => {
    const currentRequest = ++requestId.current;
    setLoading(true);
    setError("");
    try {
      const result = await request<SetupSnapshot>("GET", "/api/admin/setup");
      if (mounted.current && requestId.current === currentRequest)
        setSetup(result);
    } catch (cause) {
      if (mounted.current && requestId.current === currentRequest)
        setError(
          cause instanceof Error
            ? cause.message
            : "Could not load setup status.",
        );
    } finally {
      if (mounted.current && requestId.current === currentRequest)
        setLoading(false);
    }
  }, []);
  useEffect(() => {
    mounted.current = true;
    void load();
    return () => {
      mounted.current = false;
      requestId.current++;
    };
  }, [load, viewerId]);
  return (
    <section className="setup-panel" aria-labelledby="setup-title">
      <div className="management-head">
        <div>
          <h2 id="setup-title">Setup readiness</h2>
          <p className="muted">
            Server checks and local account counts. Unverified checks still need
            live confirmation.
          </p>
        </div>
        <button
          type="button"
          className="secondary"
          disabled={loading}
          onClick={() => void load()}
        >
          <RefreshCw size={16} /> {loading ? "Checking…" : "Refresh status"}
        </button>
      </div>
      {error && (
        <p className="field-error" role="alert">
          {error}
        </p>
      )}
      {loading && !setup && <p className="muted">Loading setup status…</p>}
      {setup && (
        <>
          <p className="setup-meta">
            {setup.environment === "demo" ? "Fictional demo · " : ""}
            Checked {new Date(setup.serverTime).toLocaleString()} · Directory
            sync{" "}
            {setup.lastDirectorySyncAt
              ? new Date(setup.lastDirectorySyncAt).toLocaleString()
              : "never"}
          </p>
          <div className="setup-checks">
            {setup.checks.map((check) => (
              <div key={check.id} className="setup-check">
                <span className={`state-pill setup-${check.status}`}>
                  {check.status.replace("_", " ")}
                </span>
                <strong>{check.label}</strong>
                <p>{check.detail}</p>
              </div>
            ))}
          </div>
          <p className="setup-counts">
            {setup.counts.users} local accounts · {setup.counts.linkedUsers} GHL
            linked · {setup.counts.unassignedUsers} unassigned ·{" "}
            {setup.counts.teams} teams · {setup.counts.pilotTeams} pilot teams ·{" "}
            {setup.counts.stagedStaff} staged staff
          </p>
        </>
      )}
    </section>
  );
}
